import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  ArrowLeft, Upload, FileSpreadsheet, CheckCircle, AlertCircle,
  Loader2, Home, User, Users, PoundSterling, Building2,
  ArrowRight, Briefcase, Scale, Calendar
} from "lucide-react";
import { API } from "@/App";

export const SalesImportPage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const fileInputRef = useRef(null);
  
  const [file, setFile] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [updateExisting, setUpdateExisting] = useState(false);
  
  const handleFileSelect = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setPreview(null);
      setResult(null);
    }
  };
  
  const handlePreview = async () => {
    if (!file) {
      toast.error("Please select a file first");
      return;
    }
    
    setPreviewing(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API}/sales/bulk-import/preview`,
        formData,
        { headers: { ...headers, 'Content-Type': 'multipart/form-data' } }
      );
      
      setPreview(response.data);
      toast.success("File preview generated");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to preview file");
    } finally {
      setPreviewing(false);
    }
  };
  
  const handleImport = async () => {
    if (!file) {
      toast.error("Please select a file first");
      return;
    }
    
    setImporting(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API}/sales/bulk-import/execute?update_existing=${updateExisting}`,
        formData,
        { headers: { ...headers, 'Content-Type': 'multipart/form-data' } }
      );
      
      setResult(response.data);
      toast.success(`Imported ${response.data.cases_created} sales cases`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to import data");
    } finally {
      setImporting(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-emerald-700 to-emerald-600 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => navigate("/sales")}
              className="text-white hover:bg-emerald-600"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">Import Sales from 10/90</h1>
              <p className="text-emerald-200">Bulk import your sales pipeline data</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Instructions */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
              How to Export from 10/90
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
              <li>In 10/90, go to <strong>Sales → Reports</strong> or <strong>Pipeline Export</strong></li>
              <li>Select all active sales or the date range you want to import</li>
              <li>Export as <strong>Excel (.xlsx)</strong> or <strong>CSV</strong></li>
              <li>Upload the file below</li>
            </ol>
            <div className="mt-4 p-3 bg-emerald-50 rounded-lg">
              <p className="text-sm text-emerald-800">
                <strong>Expected columns:</strong> Property Address, Postcode, Offer Price, Seller Name, 
                Buyer Name, Seller Solicitor, Buyer Solicitor, Status, Target Exchange Date, etc.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Upload Section */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Upload File</CardTitle>
            <CardDescription>Select your 10/90 sales export file</CardDescription>
          </CardHeader>
          <CardContent>
            <div 
              className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center cursor-pointer hover:border-emerald-500 transition-colors"
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileSelect}
                className="hidden"
              />
              {file ? (
                <div className="flex items-center justify-center gap-3">
                  <FileSpreadsheet className="w-10 h-10 text-emerald-600" />
                  <div className="text-left">
                    <p className="font-medium">{file.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {(file.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  <Upload className="w-12 h-12 mx-auto text-slate-400 mb-4" />
                  <p className="text-muted-foreground">
                    Click to select file or drag and drop
                  </p>
                  <p className="text-sm text-slate-400 mt-1">
                    Supports .xlsx, .xls, .csv
                  </p>
                </>
              )}
            </div>
            
            {file && !preview && !result && (
              <div className="mt-4 flex justify-end">
                <Button onClick={handlePreview} disabled={previewing}>
                  {previewing ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <ArrowRight className="w-4 h-4 mr-2" />
                  )}
                  Preview Import
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Preview Section */}
        {preview && !result && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
                Preview: {preview.total_rows} Sales Cases Found
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Column Mappings */}
              <div className="mb-6">
                <h4 className="font-medium mb-3">Detected Columns</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {preview.detected_columns?.map((col, idx) => (
                    <Badge key={idx} variant="outline" className="justify-start">
                      {col}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Field Mappings */}
              <div className="mb-6">
                <h4 className="font-medium mb-3">Field Mappings</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {Object.entries(preview.field_mappings || {}).map(([field, column]) => (
                    <div key={field} className="flex items-center gap-2 text-sm">
                      <Badge variant="secondary">{column}</Badge>
                      <ArrowRight className="w-4 h-4 text-slate-400" />
                      <span className="font-medium">{field.replace(/_/g, ' ')}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Sample Data */}
              {preview.sample_cases?.length > 0 && (
                <div className="mb-6">
                  <h4 className="font-medium mb-3">Sample Cases (First 5)</h4>
                  <div className="space-y-2">
                    {preview.sample_cases.map((saleCase, idx) => (
                      <div key={idx} className="p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-medium">{saleCase.address}</p>
                            <p className="text-sm text-muted-foreground">{saleCase.postcode}</p>
                          </div>
                          <Badge variant="outline">
                            <PoundSterling className="w-3 h-3 mr-1" />
                            {saleCase.offer_price ? Number(saleCase.offer_price).toLocaleString() : 'N/A'}
                          </Badge>
                        </div>
                        <div className="mt-2 flex gap-4 text-xs text-muted-foreground">
                          <span><User className="w-3 h-3 inline mr-1" />Seller: {saleCase.seller_name || 'N/A'}</span>
                          <span><Users className="w-3 h-3 inline mr-1" />Buyer: {saleCase.buyer_name || 'N/A'}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Import Options */}
              <div className="flex items-center space-x-2 mb-6 p-3 bg-amber-50 rounded-lg">
                <Checkbox 
                  id="updateExisting" 
                  checked={updateExisting}
                  onCheckedChange={setUpdateExisting}
                />
                <Label htmlFor="updateExisting" className="text-sm">
                  Update existing cases if address matches (otherwise skip duplicates)
                </Label>
              </div>

              {/* Import Button */}
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => { setPreview(null); setFile(null); }}>
                  Cancel
                </Button>
                <Button onClick={handleImport} disabled={importing} className="bg-emerald-600 hover:bg-emerald-700">
                  {importing ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  Import {preview.total_rows} Cases
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Result Section */}
        {result && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
                Import Complete
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="text-center p-4 bg-emerald-50 rounded-lg">
                  <Briefcase className="w-8 h-8 mx-auto text-emerald-600 mb-2" />
                  <p className="text-2xl font-bold text-emerald-700">{result.cases_created}</p>
                  <p className="text-sm text-muted-foreground">Cases Created</p>
                </div>
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <Users className="w-8 h-8 mx-auto text-blue-600 mb-2" />
                  <p className="text-2xl font-bold text-blue-700">{result.parties_created}</p>
                  <p className="text-sm text-muted-foreground">Parties Added</p>
                </div>
                <div className="text-center p-4 bg-amber-50 rounded-lg">
                  <ArrowRight className="w-8 h-8 mx-auto text-amber-600 mb-2" />
                  <p className="text-2xl font-bold text-amber-700">{result.cases_updated || 0}</p>
                  <p className="text-sm text-muted-foreground">Cases Updated</p>
                </div>
                <div className="text-center p-4 bg-red-50 rounded-lg">
                  <AlertCircle className="w-8 h-8 mx-auto text-red-600 mb-2" />
                  <p className="text-2xl font-bold text-red-700">{result.errors?.length || 0}</p>
                  <p className="text-sm text-muted-foreground">Errors</p>
                </div>
              </div>

              {result.errors?.length > 0 && (
                <div className="mb-6 p-4 bg-red-50 rounded-lg">
                  <h4 className="font-medium text-red-800 mb-2">Errors</h4>
                  <ul className="text-sm text-red-700 space-y-1">
                    {result.errors.slice(0, 10).map((err, idx) => (
                      <li key={idx}>• {err}</li>
                    ))}
                    {result.errors.length > 10 && (
                      <li className="text-red-500">... and {result.errors.length - 10} more</li>
                    )}
                  </ul>
                </div>
              )}

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => { setResult(null); setPreview(null); setFile(null); }}>
                  Import More
                </Button>
                <Button onClick={() => navigate("/sales")} className="bg-emerald-600 hover:bg-emerald-700">
                  <ArrowRight className="w-4 h-4 mr-2" />
                  View Sales Pipeline
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default SalesImportPage;
